// ════════════════════════════════════════════════════════════════════════════
// NovoBlocoModal — r140
// Modal de confirmação quando o usuário solta uma tarefa no calendário.
// Mostra:
//  - Tarefa que foi arrastada (read-only)
//  - Data início (= slot soltado, editável)
//  - Data fim    (= início + horas estimadas, editável)
//  - Tipo: Planejada (futuro) ou Apontada (passado) — calculado automático
//  - Descrição (opcional, só pra apontamento)
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';

const COR = {
  azul:    '#003399',
  azulBg:  '#eff6ff',
  borda:   '#e8eaed',
  texto:   '#1a1a2e',
  muted:   '#888',
  branco:  '#fff',
  verde:   '#1D9E75',
  verdeBg: '#f0fff4',
  amarelo: '#d97706',
  amlBg:   '#fffbeb',
  vermelho:'#e53935',
  cinzaBg: '#fafbfc',
};

// Helpers de data
// r144: extração direta dos componentes locais (sem cálculo de timezone offset)
// Garante que o modal mostra exatamente a hora do slot onde o usuário soltou.
function dataParaInputDatetime(d) {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm   = String(dt.getMonth() + 1).padStart(2, '0');
  const dd   = String(dt.getDate()    ).padStart(2, '0');
  const hh   = String(dt.getHours()   ).padStart(2, '0');
  const mi   = String(dt.getMinutes() ).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}
function isoToBackend(s) {
  // Mantém local: passa direto, sem Z
  return s;
}

function NovoBlocoModal({ aberto, tarefa, slotInicio, onConfirmar, onCancelar, salvando }) {
  // Estados do form
  const [dtInicio, setDtInicio] = useState('');
  const [dtFim,    setDtFim   ] = useState('');
  const [descricao, setDescricao] = useState('');
  const [faturavel, setFaturavel] = useState('');   // r175: 'S' | 'N' (só quando executada)

  useEffect(() => {
    if (!aberto) return;
    const inicio = new Date(slotInicio);
    const horasEst = parseFloat(tarefa?.horas_estimadas) || 1; // default 1h
    const fim = new Date(inicio.getTime() + horasEst * 60 * 60 * 1000);
    setDtInicio(dataParaInputDatetime(inicio));
    setDtFim(dataParaInputDatetime(fim));
    setDescricao('');
    setFaturavel('');
  }, [aberto, slotInicio, tarefa]);

  // Calcula tipo automaticamente (planejada se futuro, apontada se passado)
  const ehFuturo = useMemo(() => {
    if (!dtInicio) return true;
    return new Date(dtInicio) > new Date();
  }, [dtInicio]);

  const horas = useMemo(() => {
    if (!dtInicio || !dtFim) return 0;
    const ms = new Date(dtFim).getTime() - new Date(dtInicio).getTime();
    return Math.round((ms / 1000 / 60 / 60) * 100) / 100;
  }, [dtInicio, dtFim]);

  if (!aberto) return null;

  // r175: se executada (não futuro), "Horas Faturáveis" é obrigatório
  const valido = dtInicio && dtFim && new Date(dtFim) > new Date(dtInicio)
    && (ehFuturo || !!faturavel);

  return (
    <div
      onClick={onCancelar}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: COR.branco,
          borderRadius: '8px',
          width: '480px', maxWidth: '90vw',
          overflow: 'hidden',
          fontFamily: "'Manrope', sans-serif",
          boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: COR.azul, color: COR.branco,
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '16px', fontWeight: '700', flex: 1 }}>
            {ehFuturo ? '📅 Planejar Tarefa' : '⏱ Apontar Horas'}
          </span>
          <button onClick={onCancelar} style={btnFechar}>×</button>
        </div>

        {/* Corpo */}
        <div style={{ padding: '16px' }}>
          {/* Tarefa */}
          <div style={{
            padding: '10px 12px', borderRadius: '5px',
            backgroundColor: COR.cinzaBg, border: `1px solid ${COR.borda}`,
            marginBottom: '14px',
          }}>
            <div style={{ fontSize: '11px', color: COR.muted, marginBottom: '4px' }}>Tarefa</div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: COR.texto }}>
              #{tarefa?.id} — {tarefa?.titulo}
            </div>
            {tarefa?.cliente_fantasia && (
              <div style={{ fontSize: '11px', color: COR.muted, marginTop: '2px' }}>
                {tarefa.cliente_fantasia}
              </div>
            )}
          </div>

          {/* Tipo (calculado) */}
          <div style={{
            padding: '8px 12px', borderRadius: '5px', marginBottom: '14px',
            backgroundColor: ehFuturo ? COR.azulBg : COR.verdeBg,
            border: `1px solid ${ehFuturo ? COR.azul : COR.verde}`,
            color: ehFuturo ? COR.azul : COR.verde,
            fontSize: '12px', fontWeight: '600',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span style={{ fontSize: '14px' }}>{ehFuturo ? '📅' : '⏱'}</span>
            <span style={{ flex: 1 }}>
              {ehFuturo
                ? 'Como início está no futuro, vai ser registrado como TAREFA PLANEJADA (não gera apontamento).'
                : 'Como início está no passado, vai ser registrado como TAREFA EXECUTADA (gera apontamento de horas).'}
            </span>
          </div>

          {/* Data início | Data fim */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <Campo label="Início">
              <input type="datetime-local" value={dtInicio}
                     onChange={e => setDtInicio(e.target.value)}
                     style={inputStyle} />
            </Campo>
            <Campo label="Fim">
              <input type="datetime-local" value={dtFim}
                     onChange={e => setDtFim(e.target.value)}
                     style={inputStyle} />
            </Campo>
          </div>

          {/* Resumo de duração */}
          <div style={{ fontSize: '11px', color: COR.muted, marginBottom: '14px' }}>
            Duração: <b style={{ color: COR.texto }}>{horas.toFixed(2)}h</b>
            {tarefa?.horas_estimadas && (
              <span> (estimativa da tarefa: {parseFloat(tarefa.horas_estimadas).toFixed(2)}h)</span>
            )}
          </div>

          {/* Descrição (só pra apontamento) */}
          {!ehFuturo && (
            <Campo label="Descrição (opcional)">
              <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
                        placeholder="O que foi feito?"
                        style={{ ...inputStyle, height: '60px', resize: 'vertical', padding: '8px 10px' }} />
            </Campo>
          )}

          {/* r175: Horas faturáveis (só pra execução; 'Sim' exige projeto na tarefa) */}
          {!ehFuturo && (
            <div style={{ marginTop: '12px' }}>
              <Campo label="Horas Faturáveis *">
                <select value={faturavel} onChange={e => setFaturavel(e.target.value)}
                        style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Selecione...</option>
                  <option value="S">Sim</option>
                  <option value="N">Não</option>
                </select>
              </Campo>
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div style={{
          padding: '12px 16px', borderTop: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button onClick={onCancelar} disabled={salvando} style={btnSec}>
            Cancelar
          </button>
          <button
            onClick={() => onConfirmar({
              dtInicio: isoToBackend(dtInicio),
              dtFim:    isoToBackend(dtFim),
              ehFuturo, descricao, faturavel,
            })}
            disabled={salvando || !valido}
            style={{ ...btnPri, opacity: (salvando || !valido) ? 0.5 : 1 }}
          >
            {salvando ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sub UI
function Campo({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600',
                      color: COR.muted, marginBottom: '4px' }}>{label}</label>
      {children}
    </div>
  );
}
const inputStyle = {
  width: '100%', height: '34px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '12px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto,
  outline: 'none', fontFamily: "'Manrope', sans-serif",
};
const btnFechar = {
  background: 'transparent', border: 'none', color: COR.branco,
  fontSize: '20px', cursor: 'pointer', padding: '0 4px',
};
const btnSec = {
  padding: '8px 16px', border: `1px solid ${COR.borda}`,
  borderRadius: '5px', backgroundColor: COR.branco, color: COR.texto,
  fontSize: '13px', fontWeight: '500', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif",
};
const btnPri = {
  padding: '8px 18px', border: `1px solid ${COR.azul}`,
  borderRadius: '5px', backgroundColor: COR.azul, color: COR.branco,
  fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif",
};

export default NovoBlocoModal;
